import React, { useEffect, useRef } from 'react';
import Utilities from '@util/Utilities';

type Props = {
	label?: string,
	ariaLabel?: string,
	isLarge?: boolean,
	onChange?: (value: boolean) => any,
	disabled?: boolean,
	defaultChecked?: boolean,
	checked?: boolean
}

const Switcher: React.FunctionComponent<Props> = (
	{
		label,
		ariaLabel,
		isLarge = false,
		onChange,
		disabled = false,
		defaultChecked = false,
		checked,
	},
) => {

	const isControlled = checked !== undefined;
	const [uncontrolledChecked, setUncontrolledChecked] = React.useState(defaultChecked);
	const isChecked = isControlled ? checked : uncontrolledChecked;
	const isFirstRender = useRef(true);

	useEffect(() => {
		if (isControlled) return;
		if (isFirstRender.current) {
			isFirstRender.current = false;
			return;
		}
		if (!!onChange) onChange(isChecked);
	}, [isChecked, isControlled]);

	const toggle = () => {
		if (disabled) return;
		if (isControlled) {
			if (!!onChange) onChange(!isChecked);
			return;
		}
		setUncontrolledChecked(prev => !prev);
	};

	const switcher = <button
		type="button"
		role="switch"
		aria-checked={isChecked}
		disabled={disabled}
		onClick={toggle}
		className={Utilities.classNames(
			isChecked ? 'bg-blue-600' : 'bg-slate-200',
			!isLarge && 'flex w-8 h-[18px] flex-none cursor-pointer rounded-full p-px ring-1 ring-inset ring-slate-900/5 transition-colors duration-200 ease-in-out focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600',
			isLarge && 'relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2',
		)}
	>
		<span className="sr-only">{ariaLabel ?? label}</span>
		<span
			aria-hidden="true"
			className={Utilities.classNames(
				isChecked && !isLarge ? 'translate-x-3.5' : 'translate-x-0',
				isChecked && isLarge ? 'translate-x-5' : 'translate-x-0',
				!isLarge && 'h-4 w-4 transform rounded-full bg-white shadow-sm ring-1 ring-slate-900/5 transition duration-200 ease-in-out',
				isLarge && 'inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out',
			)}
		/>
	</button>;

	return !!label ? <div className="flex items-center justify-between mr-2">
		<div className="flex flex-col mr-2">
			<p className="text-xs font-medium text-gray-800 cursor-pointer"
			   onClick={toggle}>{label}</p>
		</div>
		{switcher}
	</div> : switcher;
};

export default Switcher;
